import { HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { filter, tap } from "rxjs/operators";
import { AppStateService } from "../services";
import {
  ApiResponseTrackerService,
  IRequestMethod,
} from "../services/api-response-tracker/api-response-tracker.service";
import { BACKGROUND_REQUEST, silentSessionExpired } from "./silent-request.token";

@Injectable({ providedIn: "root" })
export class HttpRequestInterceptor implements HttpInterceptor {
  constructor(
    private apiResponseTrackerService: ApiResponseTrackerService,
    private appStateService: AppStateService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler) {
    // Modify URL to include country code for v1 APIs
    let url = req.url;
    if (
      url.includes("/api/v1/") &&
      !url.includes("/api/v1/auth") &&
      !url.includes("/api/v1/countries")
    ) {
      // Extract the part after /api/v1/
      const parts = url.split("/api/v1/");
      if (parts.length > 1) {
        url = `${parts[0]}/api/v1/${parts[1]}`;
      }
    }

    // FormData bodies (multipart uploads, e.g. profile picture / product videos) must pass
    // through untouched: spreading a FormData instance (`{ ...req.body }`) strips it down to
    // `{}` since FormData exposes no own enumerable properties, and forcing a JSON
    // content-type overrides the multipart boundary the browser sets automatically. JSON
    // bodies keep the existing behavior.
    const isFormData = req.body instanceof FormData;

    const clonedAuthReq = req.clone({
      url,
      // Session auth is now the httpOnly `mb_session` cookie (no more x-access-token
      // header) — every request needs to carry it.
      withCredentials: true,
      setHeaders: isFormData
        ? { Accept: "application/json" }
        : {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            "Content-Type": "application/json; charset=utf-8",
            // eslint-disable-next-line @typescript-eslint/naming-convention
            Accept: "application/json",
          },
      body: isFormData ? req.body : { ...req.body },
    });

    return next.handle(clonedAuthReq).pipe(
      filter((event: any) => event instanceof HttpResponse),
      tap((event: any) => {
        this.apiResponseTrackerService.saveResponse({
          url: event.url!,
          data: event.body,
          method: req.method as IRequestMethod,
        });
        // An httpOnly cookie can't be inspected/cleared from JS, so a full re-bootstrap
        // (hard navigation) is the reliable way to resync state once the session is gone.
        // Skip it while already on /login so a fresh, logged-out visit to that page (whose
        // own bootstrap session-check also comes back Unauthorized) doesn't force-reload.
        if (
          event.body?.message === "Unauthorized" &&
          !window.location.pathname.startsWith("/login")
        ) {
          // A background poll doesn't yank the page away: it flags the expiry so pollers
          // stop; the next user action redirects as below.
          if (req.context.get(BACKGROUND_REQUEST)) {
            silentSessionExpired.set(true);
            return;
          }
          localStorage.clear();
          this.appStateService.clearUserSession();
          window.location.href = "/login";
        }
      })
    );
  }
}
